// src/redux/slices/partnersSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPartnerData,
  fetchAllClientsWithWorkouts,
  grantClientPro,
  revokeClientPro,
  type ClientWithWorkouts,
  type PartnerDataPayload,
  updateTrainerGroups,
  updateTrainerEvents,
  updateTrainerCustomExercises,
  deleteTrainerTemplate,
  upsertTrainerTemplate,
} from "../thunks/thunk-partners";

import {
  fetchRecipesAndMealPlans,
  upsertRecipe,
  deleteRecipeThunk,
} from "../thunks/thunk-recipes";

import { User as Partner, User, Recipe, MealPlans } from "../../models/types";

// ─────────────────────────────────────────────────────────────
// Mini-chat types & initial state (persist across navigation)
// ─────────────────────────────────────────────────────────────
export type MiniChatMessage = { role: "user" | "assistant"; content: string };

const initialMiniChatGreeting: MiniChatMessage = {
  role: "assistant",
  content: "Hey there Coach!\nAsk me anything about your clients.",
};

const initialMiniChatState = {
  messages: [initialMiniChatGreeting] as MiniChatMessage[],
  draft: "" as string,
};

// Define a type for partner clients as defined in your User interface.
interface PartnerClient {
  clientUserId: string;
  name: string;
  gender: "male" | "female";
  birthday: Date;
  height: number;
  weight: number;
  activityLevel:
    | "sedentary"
    | "lightly active"
    | "moderately active"
    | "very active"
    | "extra active";
}

interface PartnersState {
  partnerStatus: "idle" | "loading" | "succeeded" | "failed";
  partnerError: string | null;

  partnerData: {
    partnerData: Partner;
    partnerCount: number;
  } | null;

  clientsData: null | PartnerClient[];
  darkMode: Boolean;
  dashboardClientSelected: User | null;

  /** Full client user records + workouts pulled from avro-workouts */
  clientsWithWorkoutsStatus: "idle" | "loading" | "succeeded" | "failed";
  clientsWithWorkoutsError: string | null;
  clientsWithWorkouts: ClientWithWorkouts[] | null;

  /** Mutation statuses for granting/revoking pro */
  proMutationStatus: "idle" | "granting" | "revoking" | "succeeded" | "failed";
  proMutationError: string | null;

  /**
   * Recipes / MealPlans vault (from avro-recipes-mealplans-api GET ?userId=...)
   */
  recipesFetchStatus: "idle" | "loading" | "succeeded" | "failed";
  recipesFetchError: string | null;

  recipes: Recipe[];
  mealPlans: MealPlans[];

  /** Recipe mutation status/errors (create/update/delete) */
  recipeMutationStatus: "idle" | "saving" | "deleting" | "succeeded" | "failed";
  recipeMutationError: string | null;

  /** NEW: mini-chat persistent state */
  miniChat: {
    messages: MiniChatMessage[];
    draft: string;
  };
}

const initialState: PartnersState = {
  partnerStatus: "idle",
  partnerError: null,
  partnerData: null,
  clientsData: null,
  dashboardClientSelected: null,
  darkMode: false,

  clientsWithWorkoutsStatus: "idle",
  clientsWithWorkoutsError: null,
  clientsWithWorkouts: null,

  proMutationStatus: "idle",
  proMutationError: null,

  recipesFetchStatus: "idle",
  recipesFetchError: null,

  recipes: [],
  mealPlans: [],

  recipeMutationStatus: "idle",
  recipeMutationError: null,

  miniChat: initialMiniChatState,
};

function computePartnerCount(p?: Partner | null): number {
  if (!p) return 0;
  const list = (p as any).partnerClients as PartnerClient[] | undefined;
  return Array.isArray(list) ? list.length : 0;
}

const partnersSlice = createSlice({
  name: "partners",
  initialState,
  reducers: {
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
    setDashboardClient: (state, action: PayloadAction<User | null>) => {
      state.dashboardClientSelected = action.payload;
    },

    // Mini-chat reducers
    setMiniChatDraft: (state, action: PayloadAction<string>) => {
      state.miniChat.draft = action.payload;
    },
    addMiniChatMessage: (state, action: PayloadAction<MiniChatMessage>) => {
      state.miniChat.messages.push(action.payload);
    },
    setMiniChatMessages: (
      state,
      action: PayloadAction<MiniChatMessage[]>
    ) => {
      state.miniChat.messages = action.payload.length
        ? action.payload
        : [initialMiniChatGreeting];
    },
    replaceLastMiniChatMessage: (
      state,
      action: PayloadAction<MiniChatMessage>
    ) => {
      if (!state.miniChat.messages.length) {
        state.miniChat.messages = [action.payload];
      } else {
        state.miniChat.messages[state.miniChat.messages.length - 1] =
          action.payload;
      }
    },
    clearMiniChat: (state) => {
      state.miniChat = {
        messages: [initialMiniChatGreeting],
        draft: "",
      };
    },
  },
  extraReducers: (builder) => {
    // ─────────────────────────────────────────
    // fetchPartnerData
    // ─────────────────────────────────────────
    builder
      .addCase(fetchPartnerData.pending, (state) => {
        state.partnerStatus = "loading";
        state.partnerError = null;
      })
      .addCase(
        fetchPartnerData.fulfilled,
        (
          state,
          action: PayloadAction<Partner, string, { arg: PartnerDataPayload }>
        ) => {
          state.partnerStatus = "succeeded";
          const method = action.meta.arg.method || "GET";

          state.partnerData = {
            partnerData: action.payload,
            partnerCount: computePartnerCount(action.payload),
          };

          state.clientsData = (action.payload as any).partnerClients
            ? [...(action.payload as any).partnerClients]
            : [];

          // 🔁 keep mealPlans in sync with the latest partner payload
          state.mealPlans =
            ((action.payload as any)?.mealPlans as MealPlans[]) ??
            state.mealPlans;

          if (method === "POST") {
            // no-op for now
          }
        }
      )
      .addCase(fetchPartnerData.rejected, (state, action) => {
        state.partnerStatus = "failed";
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch partner data.";
      });

    // ─────────────────────────────────────────
    // fetchAllClientsWithWorkouts
    // ─────────────────────────────────────────
    builder
      .addCase(fetchAllClientsWithWorkouts.pending, (state) => {
        state.clientsWithWorkoutsStatus = "loading";
        state.clientsWithWorkoutsError = null;
      })
      .addCase(
        fetchAllClientsWithWorkouts.fulfilled,
        (state, action: PayloadAction<unknown[]>) => {
          state.clientsWithWorkoutsStatus = "succeeded";
          const casted = action.payload as ClientWithWorkouts[];
          state.clientsWithWorkouts = casted ?? [];
        }
      )
      .addCase(fetchAllClientsWithWorkouts.rejected, (state, action) => {
        state.clientsWithWorkoutsStatus = "failed";
        state.clientsWithWorkoutsError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch clients with workouts.";
      });

    // ─────────────────────────────────────────
    // updateTrainerGroups (optimistic)
    // ─────────────────────────────────────────
    builder
      .addCase(updateTrainerGroups.fulfilled, (state, action) => {
        state.partnerData = {
          partnerData: action.payload as Partner,
          partnerCount: computePartnerCount(action.payload as Partner),
        };
        const partnerClients = (action.payload as any)?.partnerClients ?? [];
        state.clientsData = partnerClients ? [...partnerClients] : [];

        // 🔁 sync mealPlans from updated partner payload (defensive)
        state.mealPlans =
          ((action.payload as any)?.mealPlans as MealPlans[]) ??
          state.mealPlans;
      })
      .addCase(updateTrainerGroups.rejected, (state, action) => {
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update trainer groups.";
      });

    // ─────────────────────────────────────────
    // updateTrainerEvents (optimistic)
    // ─────────────────────────────────────────
    builder
      .addCase(updateTrainerEvents.fulfilled, (state, action) => {
        state.partnerData = {
          partnerData: action.payload as Partner,
          partnerCount: computePartnerCount(action.payload as Partner),
        };

        // 🔁 sync mealPlans from updated partner payload (defensive)
        state.mealPlans =
          ((action.payload as any)?.mealPlans as MealPlans[]) ??
          state.mealPlans;
      })
      .addCase(updateTrainerEvents.rejected, (state, action) => {
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update trainer events.";
      });

    // ─────────────────────────────────────────
    // grantClientPro / revokeClientPro
    // ─────────────────────────────────────────
    builder
      .addCase(grantClientPro.pending, (state) => {
        state.proMutationStatus = "granting";
        state.proMutationError = null;
      })
      .addCase(
        grantClientPro.fulfilled,
        (state, action: PayloadAction<{ client: User; partner: User }>) => {
          state.proMutationStatus = "succeeded";

          state.partnerData = {
            partnerData: action.payload.partner as Partner,
            partnerCount: computePartnerCount(
              action.payload.partner as Partner
            ),
          };

          const partnerClients = (action.payload.partner as any)
            .partnerClients as PartnerClient[] | undefined;
          state.clientsData = partnerClients ? [...partnerClients] : [];

          // 🔁 sync mealPlans from updated partner payload (defensive)
          state.mealPlans =
            ((action.payload.partner as any)?.mealPlans as MealPlans[]) ??
            state.mealPlans;

          // sync pro flags into clientsWithWorkouts
          if (state.clientsWithWorkouts) {
            const cid = (action.payload.client as any).userId as string;
            const idx = state.clientsWithWorkouts.findIndex(
              (c) => c?.clientUserId === cid || c?.userId === cid
            );
            if (idx >= 0) {
              (state.clientsWithWorkouts[idx] as any).isProUser = true;
              (state.clientsWithWorkouts[idx] as any).currentTrainer = (
                action.payload.client as any
              ).currentTrainer;
            }
          }
        }
      )
      .addCase(grantClientPro.rejected, (state, action) => {
        state.proMutationStatus = "failed";
        state.proMutationError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to grant pro access.";
      });

    builder
      .addCase(revokeClientPro.pending, (state) => {
        state.proMutationStatus = "revoking";
        state.proMutationError = null;
      })
      .addCase(
        revokeClientPro.fulfilled,
        (state, action: PayloadAction<{ client: User; partner: User }>) => {
          state.proMutationStatus = "succeeded";

          state.partnerData = {
            partnerData: action.payload.partner as Partner,
            partnerCount: computePartnerCount(
              action.payload.partner as Partner
            ),
          };

          const partnerClients = (action.payload.partner as any)
            .partnerClients as PartnerClient[] | undefined;
          state.clientsData = partnerClients ? [...partnerClients] : [];

          // 🔁 sync mealPlans from updated partner payload (defensive)
          state.mealPlans =
            ((action.payload.partner as any)?.mealPlans as MealPlans[]) ??
            state.mealPlans;

          // sync pro flags into clientsWithWorkouts
          if (state.clientsWithWorkouts) {
            const cid = (action.payload.client as any).userId as string;
            const idx = state.clientsWithWorkouts.findIndex(
              (c) => c?.clientUserId === cid || c?.userId === cid
            );
            if (idx >= 0) {
              (state.clientsWithWorkouts[idx] as any).isProUser = false;
              (state.clientsWithWorkouts[idx] as any).currentTrainer = null;
            }
          }
        }
      )
      .addCase(revokeClientPro.rejected, (state, action) => {
        state.proMutationStatus = "failed";
        state.proMutationError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to revoke pro access.";
      });

    // ─────────────────────────────────────────
    // updateTrainerCustomExercises
    // ─────────────────────────────────────────
    builder
      .addCase(updateTrainerCustomExercises.fulfilled, (state, action) => {
        state.partnerData = {
          partnerData: action.payload as Partner,
          partnerCount: computePartnerCount(action.payload as Partner),
        };

        // 🔁 sync mealPlans from updated partner payload (defensive)
        state.mealPlans =
          ((action.payload as any)?.mealPlans as MealPlans[]) ??
          state.mealPlans;
      })
      .addCase(updateTrainerCustomExercises.rejected, (state, action) => {
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update custom exercises.";
      });

    // ─────────────────────────────────────────
    // templates (upsertTrainerTemplate / deleteTrainerTemplate)
    // ─────────────────────────────────────────
    builder
      .addCase(upsertTrainerTemplate.fulfilled, (state, action) => {
        state.partnerData = {
          partnerData: action.payload as Partner,
          partnerCount: computePartnerCount(action.payload as Partner),
        };

        // 🔁 sync mealPlans from updated partner payload (defensive)
        state.mealPlans =
          ((action.payload as any)?.mealPlans as MealPlans[]) ??
          state.mealPlans;
      })
      .addCase(upsertTrainerTemplate.rejected, (state, action) => {
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to create/update template.";
      })
      .addCase(deleteTrainerTemplate.fulfilled, (state, action) => {
        state.partnerData = {
          partnerData: action.payload as Partner,
          partnerCount: computePartnerCount(action.payload as Partner),
        };

        // 🔁 sync mealPlans from updated partner payload (defensive)
        state.mealPlans =
          ((action.payload as any)?.mealPlans as MealPlans[]) ??
          state.mealPlans;
      })
      .addCase(deleteTrainerTemplate.rejected, (state, action) => {
        state.partnerError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to delete template.";
      });

    // ─────────────────────────────────────────
    // RECIPES / MEAL PLANS FETCH (GET ?userId=...)
    // ─────────────────────────────────────────
    builder
      .addCase(fetchRecipesAndMealPlans.pending, (state) => {
        state.recipesFetchStatus = "loading";
        state.recipesFetchError = null;
      })
      .addCase(fetchRecipesAndMealPlans.fulfilled, (state, action) => {
        state.recipesFetchStatus = "succeeded";
        state.recipes = action.payload.recipes || [];
        state.mealPlans = action.payload.mealPlans || [];
      })
      .addCase(fetchRecipesAndMealPlans.rejected, (state, action) => {
        state.recipesFetchStatus = "failed";
        state.recipesFetchError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch recipes/meal plans.";
      });

    // ─────────────────────────────────────────
    // RECIPE UPSERT (POST { userId, recipes:[...] })
    // ─────────────────────────────────────────
    builder
      .addCase(upsertRecipe.pending, (state) => {
        state.recipeMutationStatus = "saving";
        state.recipeMutationError = null;
      })
      .addCase(upsertRecipe.fulfilled, (state, action) => {
        state.recipeMutationStatus = "succeeded";

        const saved = (action.payload as any).recipe;
        if (!saved) return;

        // normalize ID reference
        const savedId = (saved as any).recipeId ?? (saved as any).id;

        // check if it already exists in state.recipes
        const idx = state.recipes.findIndex(
          (r: any) => r.recipeId === savedId || r.id === savedId
        );

        if (idx >= 0) {
          // update in place
          state.recipes[idx] = saved;
        } else {
          // prepend new recipe
          state.recipes.unshift(saved);
        }
      })
      .addCase(upsertRecipe.rejected, (state, action) => {
        state.recipeMutationStatus = "failed";
        state.recipeMutationError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to save recipe.";
      });

    // ─────────────────────────────────────────
    // RECIPE DELETE (DELETE ?userId=...&recipeId=...)
    // ─────────────────────────────────────────
    builder
      .addCase(deleteRecipeThunk.pending, (state) => {
        state.recipeMutationStatus = "deleting";
        state.recipeMutationError = null;
      })
      .addCase(deleteRecipeThunk.fulfilled, (state, action) => {
        state.recipeMutationStatus = "succeeded";

        const { recipeId } = action.payload;
        state.recipes = state.recipes.filter(
          (r: any) => r.recipeId !== recipeId && r.id !== recipeId
        );
      })
      .addCase(deleteRecipeThunk.rejected, (state, action) => {
        state.recipeMutationStatus = "failed";
        state.recipeMutationError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to delete recipe.";
      });
  },
});

export const {
  toggleDarkMode,
  setDashboardClient,

  // mini-chat actions
  setMiniChatDraft,
  addMiniChatMessage,
  setMiniChatMessages,
  replaceLastMiniChatMessage,
  clearMiniChat,
} = partnersSlice.actions;

export default partnersSlice.reducer;
